import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { addSampleControlGroup, addSampleControlGroupVariables, DeleteManySampleControlGroups, DeleteManySampleControlGroupsVariables, GetAllSampleControls, GetAllSampleControlsVariables, GetSampleControlGroup, GetSampleControlGroupVariables, RemoveSampleControlGroup, RemoveSampleControlGroupVariables, sampleControlGroupExists, sampleControlGroupExistsVariables, sampleControlGroupInput, sampleControlGroups, updateSampleControlGroup, updateSampleControlGroupVariables } from '@modelos/interfaces/operation-result-types';
import { Add_SampleControlGroupAnalysis, Get_AllSampleControlGroupAnalysis, Remove_SampleControlGroupAnalysis, Update_SampleControlGroupAnalysis } from 'app/graphql/sampleControlGroupAnalysis.graphql';
import { Get_AssayGroups } from 'app/graphql/assayGroup.graphql';
import { Get_Assays } from 'app/graphql/assay.graphql';
import { Add_SampleControlGroup, Delete_ManySampleControlGroups, Get_AllSampleControls, Get_SampleControlGroup, Remove_SampleControlGroup, SampleControlGroupExists, Update_SampleControlGroup } from 'app/graphql/sampleControls.graphql';



@Injectable({
  providedIn: 'root'
})
export class SampleControlTableService {

  private apollo = inject(Apollo);


  async getById(id: string) {
    try {
      const result = await firstValueFrom(
        this.apollo.query<GetSampleControlGroup, GetSampleControlGroupVariables>({
          query: Get_SampleControlGroup,
          variables: {
            id
          },
          fetchPolicy: 'network-only'
        })
      );

      const data = result.data;
      if (data && data.sampleControlGroup) {
        return data.sampleControlGroup;
      }

      throw new Error(`Price list with id ${id} not found`);
    } catch (error) {
      console.error(`Error fetching price list with id ${id}`, error);
      throw error;
    }
  }

  async save(item: sampleControlGroupInput) {
    try {
      const result = await firstValueFrom(
        this.apollo.mutate<addSampleControlGroup, addSampleControlGroupVariables>({
          mutation: Add_SampleControlGroup,
          variables: {
            data: {
              name: item.name,
              code: item.code,
              active: item.active,
              matrix: item.matrix,
              v_: item.v_
            }
          }
        })
      );

      const data = result.data;

      if (data?.addSampleControlGroup) return data.addSampleControlGroup;

      throw new Error('No data returned from GraphQL mutation');

    } catch (error) {
      console.error('Error saving sample control group', error);
      throw error;
    }
  }

  async update(item: sampleControlGroupInput) {
    try {
      const result = await firstValueFrom(
        this.apollo.mutate<updateSampleControlGroup, updateSampleControlGroupVariables>({
          mutation: Update_SampleControlGroup,
          variables: {
            id: item.id,
            data: {
              name: item.name,
              code: item.code,
              active: item.active,
              matrix: item.matrix,
              v_: item.v_
            }
          }
        })
      );

      const data = result.data;

      if (data?.updateSampleControlGroup) return data.updateSampleControlGroup;

      throw new Error('No data returned from GraphQL mutation');

    } catch (error) {
      console.error('Error updating sample control group', error);
      throw error;
    }
  }

  async delete(id: string, v_: number) {
    try {
      const result = await firstValueFrom(
        this.apollo.mutate<RemoveSampleControlGroup, RemoveSampleControlGroupVariables>({
          mutation: Remove_SampleControlGroup,
          variables: {
            id,
            v_
          }
        })
      );

      const data = result.data;

      if (data?.deleteSampleControlGroup) return data.deleteSampleControlGroup;

      throw new Error('No data returned from GraphQL mutation');

    } catch (error) {
      console.error('Error deleting sample control group', error);
      throw error;
    }
  }

  async deleteMany(idsV_: { id: string, v_: number }[]) {
    try {
      const result = await firstValueFrom(
        this.apollo.mutate<DeleteManySampleControlGroups, DeleteManySampleControlGroupsVariables>({
          mutation: Delete_ManySampleControlGroups,
          variables: {
            idsV_
          }
        })
      );

      const data = result.data;

      if (data?.removeManySampleControlGroups) return data.removeManySampleControlGroups;

      throw new Error('No data returned from GraphQL mutation');

    } catch (error) {
      console.error('Error deleting sample control group', error);
      throw error;
    }
  }

  async codeExists(code: string): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        this.apollo.query<sampleControlGroupExists, sampleControlGroupExistsVariables>({
          query: SampleControlGroupExists,
          variables: {
            code
          },
          fetchPolicy: 'network-only'
        })
      );

      return result.data?.sampleControlGroupExists ?? false;
    } catch (error) {
      console.error('Error checking if sample control group code exists', error);
      throw error;
    }
  }

  async getSampleControlGroups(options: {
    page?: number;
    limit?: number;
    search?: string;
    filters?: any;
    sortBy?: string;
    sortOrder?: string;
  } = {}) {
    const { page = 1, limit = 10, search, filters, sortBy, sortOrder } = options;

    try {
      const result = await firstValueFrom(
        this.apollo.query<any>({
          query: Get_AllSampleControls,
          variables: {
            options: {
              page,
              limit,
              search: search || undefined,
              filters: JSON.stringify(filters) || "{}",
              sortBy,
              sortOrder
            }
          },
          fetchPolicy: 'network-only'
        })
      );
      const data = result.data;
      if (data && data.sampleControlGroups) {
        return {
          response: data.sampleControlGroups.data,
          total: data.sampleControlGroups.total,
          limit: data.sampleControlGroups.limit
        };
      }
      throw new Error('No data returned from GraphQL query');
    } catch (error) {
      console.error('Error fetching sample control groups', error);
      throw error;
    }
  }
}

